import Fieldset from 'components/_UI/Fieldset'
import fieldValidations from 'helpers/fieldValidations'
import masks from 'helpers/masks'
import React from 'react'
import styled from 'styled-components'
import { Column } from 'styled/alignment/Column'
import breakpoints from 'theme/breakpoints'
import colors from 'theme/colors'
import { Mail, FileText, Lock } from 'lucide-react'

function formatCPF(doc) {
    if (!doc) return ''
    const digits = String(doc).replace(/\D/g, '')
    if (digits.length !== 11) return doc
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`
}

export default function BasicInfo({ register, errors, user }) {
    const hasDocument = !!user?.document

    return (
        <Container>
            <ContentFields>
                <SectionTitle>Informações básicas</SectionTitle>

                <FieldGroup>
                    <FieldLabel>Nome de usuário <Required>*</Required></FieldLabel>
                    <Fieldset
                        name='username'
                        register={register}
                        placeholder='nome_usuario'
                        validations={fieldValidations.name('nome de usuário')}
                    />
                    {errors?.username?.message && <ErrorText>{errors.username.message}</ErrorText>}
                </FieldGroup>

                <FieldRow>
                    <FieldGroup>
                        <FieldLabel>Primeiro nome <Required>*</Required></FieldLabel>
                        <Fieldset
                            name='firstName'
                            register={register}
                            placeholder='Seu nome'
                            validations={fieldValidations.name('primeiro nome')}
                        />
                        {errors?.firstName?.message && <ErrorText>{errors.firstName.message}</ErrorText>}
                    </FieldGroup>

                    <FieldGroup>
                        <FieldLabel>Último nome <Required>*</Required></FieldLabel>
                        <Fieldset
                            name='lastName'
                            register={register}
                            placeholder='Sobrenome'
                            validations={fieldValidations.name('último nome')}
                        />
                        {errors?.lastName?.message && <ErrorText>{errors.lastName.message}</ErrorText>}
                    </FieldGroup>
                </FieldRow>

                <FieldGroup>
                    <FieldLabel>Bio</FieldLabel>
                    <Fieldset
                        name='description'
                        register={register}
                        placeholder='Conte um pouco sobre você (máx. 120 caracteres)'
                        validations={fieldValidations.description('descrição', 120)}
                    />
                    {errors?.description?.message && <ErrorText>{errors.description.message}</ErrorText>}
                </FieldGroup>

                <Divider />
                <SectionTitle>Contato</SectionTitle>

                <FieldGroup>
                    <FieldLabel>E-mail</FieldLabel>
                    <ReadOnlyField>
                        <ReadOnlyIcon><Mail size={14} color={colors.night} /></ReadOnlyIcon>
                        <ReadOnlyValue>{user?.email || '—'}</ReadOnlyValue>
                        <ReadOnlyBadge><Lock size={12} color={colors.nightLight} /></ReadOnlyBadge>
                    </ReadOnlyField>
                </FieldGroup>

                <FieldGroup>
                    <FieldLabel>CPF</FieldLabel>
                    {hasDocument ? (
                        <ReadOnlyField>
                            <ReadOnlyIcon><FileText size={14} color={colors.night} /></ReadOnlyIcon>
                            <ReadOnlyValue>{formatCPF(user.document)}</ReadOnlyValue>
                            <ReadOnlyBadge><Lock size={12} color={colors.nightLight} /></ReadOnlyBadge>
                        </ReadOnlyField>
                    ) : (
                        <>
                            <Fieldset
                                name='cpf'
                                register={register}
                                placeholder='000.000.000-00'
                                mask={masks.cpf}
                                validations={fieldValidations.cpf()}
                            />
                            {errors?.cpf?.message && <ErrorText>{errors.cpf.message}</ErrorText>}
                        </>
                    )}
                </FieldGroup>
            </ContentFields>
        </Container>
    )
}

const Container = styled(Column)`
    width: 100%;
    margin-top: 4px;
    align-items: center;
`

const ContentFields = styled(Column)`
    width: 100%;
    align-items: stretch;

    @media screen and (min-width: ${breakpoints.sm}) {
        width: 500px;
    }
`

const SectionTitle = styled.p`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${colors.nightLight};
    margin: 0 0 12px;
`

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 14px;
    width: 100%;
`

const FieldRow = styled.div`
    display: flex;
    gap: 12px;
    width: 100%;

    @media screen and (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        gap: 0;
    }
`

const FieldLabel = styled.label`
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 6px;
    display: block;
`

const Required = styled.span`
    color: ${colors.danger};
`

const ErrorText = styled.p`
    font-size: 12px;
    color: ${colors.danger};
    margin-top: 4px;
`

const Divider = styled.div`
    height: 1px;
    background: ${colors.neutralDark};
    margin: 8px 0 20px;
`

const ReadOnlyField = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: ${colors.neutralDark};
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 8px;
    min-height: 44px;
`

const ReadOnlyIcon = styled.span`
    display: flex;
    align-items: center;
    flex-shrink: 0;
`

const ReadOnlyValue = styled.span`
    font-size: 14px;
    color: ${colors.night};
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const ReadOnlyBadge = styled.span`
    display: flex;
    align-items: center;
    flex-shrink: 0;
`
